'use client';

import { useToast } from '@/components/ui/use-toast';
import { useAuthV2 } from '@/hooks/useAuthV2';
import { getMessagingIfSupported, onMessage } from '@/lib/firebase-config';
import { deleteFCMToken, saveFCMToken } from '@/services/fcmTokenService';
import { requestNotificationPermission } from '@/utils/requestNotificationPermission';
import { useCallback, useEffect, useRef, useState } from 'react';

function getIOSNonPWA(): boolean {
  if (typeof window === 'undefined') return false;
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isStandalone = 'standalone' in navigator && (navigator as any).standalone === true;
  return isIOS && !isStandalone;
}

export function useNotifications() {
  const [localPermission, setLocalPermission] = useState<NotificationPermission>('default');
  const [token, setToken] = useState<string | null>(null);
  const [tokenId, setTokenId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const hasAttemptedAutoToken = useRef(false);

  // Sync isDisabled state from localStorage after mount
  useEffect(() => {
    const stored = localStorage.getItem('notifications-disabled');
    if (stored === 'true') {
      setIsDisabled(true);
    }
  }, []);
  const { showToast, showNotificationToast } = useToast();
  const { user } = useAuthV2();

  // Computed permission que considera tanto a permissão do navegador quanto o estado local
  const permission = isDisabled ? 'default' : localPermission;

  // console.log('Current FCM Token:', token);
  // console.log('Current Token ID:', tokenId);

  useEffect(() => {
    // Check current permission status
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setLocalPermission(Notification.permission);

      // Se já tem permissão mas não tem token, tentar obter (uma única vez)
      if (Notification.permission === 'granted' && !token && !isDisabled && !hasAttemptedAutoToken.current) {
        hasAttemptedAutoToken.current = true;
        console.log('Permission granted but no token, attempting to get existing token...');
        requestNotificationPermission().then((existingToken) => {
          if (existingToken) {
            setToken(existingToken);
            // Sincronizar com backend se user estiver logado e não houver tokenId
            if (user?.id && !tokenId) {
              saveFCMToken({ token: existingToken, userId: user.id })
                .then((saved) => setTokenId(saved.id))
                .catch((err) => console.warn('[FCM] Failed to sync token on mount:', err));
            }
          }
        });
      }
    }

    // Listen for foreground messages and show as toast
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    getMessagingIfSupported().then((messaging) => {
      if (!messaging || cancelled) return;

      unsubscribe = onMessage(messaging, (payload) => {
        console.log('Message received in foreground:', payload);

        const title = payload.notification?.title || 'MASP Bilheteria';
        const body = payload.notification?.body || 'Você recebeu uma nova notificação';
        const imageUrl = payload.data?.imageUrl || payload.notification?.image;
        const actionUrl = payload.data?.url;

        // Native OS notification (banner on macOS, push on mobile, etc.)
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
          try {
            const options: NotificationOptions & { image?: string } = {
              body,
              icon: imageUrl || '/notification-icon.svg',
              badge: '/notification-icon.svg',
              tag: 'fcm-foreground',
              data: { url: actionUrl },
            };
            if (imageUrl) options.image = imageUrl;
            const nativeNotification = new Notification(title, options);

            nativeNotification.onclick = () => {
              window.focus();
              window.open(actionUrl || '/', '_self');
              nativeNotification.close();
            };
          } catch (err) {
            console.warn('[Notifications] Native notification failed:', err);
          }
        }

        // In-page toast (complementary)
        showNotificationToast({
          title,
          description: body,
          imageUrl,
          duration: 8000,
          onClick: () => window.open(actionUrl || '/', '_self'),
        });
      });
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast, showNotificationToast, user?.id]);

  const requestPermission = useCallback(async () => {
    setIsLoading(true);

    try {
      const receivedToken = await requestNotificationPermission();
      // console.log('Received token:', receivedToken);

      if (receivedToken) {
        setToken(receivedToken);
        setLocalPermission('granted');
        setIsDisabled(false);
        localStorage.setItem('notifications-disabled', 'false');

        // Salvar token no backend
        try {
          const savedToken = await saveFCMToken({
            token: receivedToken,
            userId: user?.id,
          });

          setTokenId(savedToken.id);

          showToast({
            title: 'Notificações ativadas!',
            description: 'Você receberá notificações push neste dispositivo.',
            variant: 'success',
            duration: 4000,
          });
        } catch (saveError) {
          console.error('Error saving FCM token:', saveError);
          showToast({
            title: 'Notificações ativadas!',
            description: 'Token salvo localmente. Sincronização pendente.',
            variant: 'success',
            duration: 4000,
          });
        }

        return receivedToken;
      } else {
        showToast({
          title: 'Erro ao ativar notificações',
          description: 'Não foi possível obter permissão para notificações.',
          variant: 'error',
          duration: 4000,
        });
        return null;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      showToast({
        title: 'Erro ao ativar notificações',
        description: 'Ocorreu um erro ao solicitar permissão para notificações.',
        variant: 'error',
        duration: 4000,
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [showToast, user?.id]);

  const removePermission = useCallback(async () => {
    console.log('Removing FCM token:', tokenId, token);
    if (!tokenId && !token) {
      return false;
    }

    setIsLoading(true);

    try {
      if (tokenId) {
        await deleteFCMToken(tokenId);
      }

      // Reset local state regardless of backend success
      setToken(null);
      setTokenId(null);
      setIsDisabled(true);
      localStorage.setItem('notifications-disabled', 'true');

      showToast({
        title: 'Notificações desativadas',
        description: 'Você não receberá mais notificações neste dispositivo.',
        variant: 'success',
        duration: 4000,
      });

      return true;
    } catch (error) {
      console.error('Error removing FCM token:', error);

      setToken(null);
      setTokenId(null);
      setIsDisabled(true);
      localStorage.setItem('notifications-disabled', 'true');

      showToast({
        title: 'Notificações desativadas',
        description: 'Token removido localmente. Sincronização pendente.',
        variant: 'success',
        duration: 4000,
      });
      return true;
    } finally {
      setIsLoading(false);
    }
  }, [tokenId, token, showToast]);

  const resetState = useCallback(() => {
    setToken(null);
    setTokenId(null);
    setIsDisabled(false);
    setLocalPermission('default');
    localStorage.removeItem('notifications-disabled');
    console.log('Notification state reset');
  }, []);

  const forceSaveToken = useCallback(async () => {
    if (!token) {
      console.log('No token available to save');
      return false;
    }

    try {
      // console.log('Forcing save of token:', token);
      const savedToken = await saveFCMToken({
        token: token,
        userId: user?.id,
      });

      setTokenId(savedToken.id);
      // console.log('Token saved successfully with ID:', savedToken.id);

      showToast({
        title: 'Token salvo!',
        description: 'Token FCM foi salvo no backend.',
        variant: 'success',
        duration: 4000,
      });

      return true;
    } catch (error) {
      console.error('Error saving token:', error);
      showToast({
        title: 'Erro ao salvar token',
        description: 'Não foi possível salvar o token no backend.',
        variant: 'error',
        duration: 4000,
      });
      return false;
    }
  }, [token, user?.id, showToast]);

  // Expor funções para testes no console
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).resetNotificationState = resetState;
      (window as any).forceSaveToken = forceSaveToken;
      (window as any).getCurrentToken = () => {
        console.log('Current Token:', token);
        console.log('Current Token ID:', tokenId);
        console.log('Permission:', permission);
        return { token, tokenId, permission };
      };
    }
  }, [resetState, forceSaveToken, token, tokenId, permission]);

  return {
    permission,
    token,
    tokenId,
    isLoading,
    isDisabled,
    isIOSNonPWA: getIOSNonPWA(),
    requestPermission,
    removePermission,
    resetState, // Para testes
    forceSaveToken, // Para forçar salvamento
  };
}
